import React from 'react';
import {
  Search,
  Bell,
  Sparkles,
  TrendingUp,
  Filter,
  MoreHorizontal,
  ChevronRight,
  Layers,
  Home,
  Users,
  BarChart3,
  Settings,
  Zap,
  ArrowUpRight,
} from 'lucide-react';

/**
 * PredictorDashboardMock — pixel-perfect flagship browser mockup of the
 * predictor.com AI-driven ABM platform. Rendered entirely in HTML/CSS/SVG,
 * used as a visual anchor on the Solution Blueprint section.
 */
export default function PredictorDashboardMock() {
  return (
    <div className="relative bg-[#0A1024] border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-3 bg-[#050A1F] border-b border-white/10">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
          <div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
          <div className="w-3 h-3 rounded-full bg-[#28C840]" />
        </div>
        <div className="flex-1 mx-4 h-7 bg-white/5 border border-white/10 rounded-md px-3 flex items-center gap-2 text-[11px] font-mono text-white/50">
          <span className="w-1.5 h-1.5 rounded-full bg-[#4ECDC4]" />
          <span className="text-[#4ECDC4]">predictor.com</span>
          <span className="text-white/30">/dashboard/pipeline</span>
        </div>
        <div className="text-[10px] font-mono text-white/40">v3.2.1</div>
      </div>

      {/* App shell */}
      <div className="flex min-h-[520px]">
        {/* Sidebar */}
        <aside className="w-16 lg:w-52 bg-[#050A1F] border-r border-white/10 py-6 flex flex-col">
          <div className="px-4 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#4ECDC4] to-[#FFB020] flex items-center justify-center">
                <Layers className="w-4 h-4 text-[#050A1F]" strokeWidth={2.5} />
              </div>
              <div className="hidden lg:block">
                <div className="text-[11px] font-mono uppercase tracking-widest text-white/40">predictor</div>
                <div className="text-xs font-semibold text-[#F1EDE4]">.com</div>
              </div>
            </div>
          </div>
          <nav className="flex-1 px-2 space-y-1">
            {[
              { icon: Home, label: 'Overview', active: false },
              { icon: TrendingUp, label: 'Pipeline', active: true },
              { icon: Users, label: 'Accounts', active: false },
              { icon: BarChart3, label: 'Signals', active: false },
              { icon: Zap, label: 'AI Studio', active: false },
              { icon: Settings, label: 'Settings', active: false },
            ].map((n) => {
              const Icon = n.icon;
              return (
                <div
                  key={n.label}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] transition-colors ${
                    n.active
                      ? 'bg-[#4ECDC4]/10 border border-[#4ECDC4]/30 text-[#4ECDC4]'
                      : 'text-white/60 hover:text-white/90'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />
                  <span className="hidden lg:inline">{n.label}</span>
                </div>
              );
            })}
          </nav>
          <div className="px-3 pt-4 border-t border-white/10 hidden lg:block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">Plan</div>
            <div className="flex items-center gap-1.5 text-[11px] text-[#FFB020]">
              <Sparkles className="w-3 h-3" /> Enterprise
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Top bar */}
          <div className="flex items-center justify-between px-6 py-3.5 border-b border-white/10">
            <div className="flex items-center gap-2 text-[11px] text-white/50">
              <span>Pipeline</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-white/80">Q1 2026 · Priority accounts</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 h-8 px-3 bg-white/5 border border-white/10 rounded-md text-[11px] text-white/50 min-w-[180px]">
                <Search className="w-3 h-3" />
                <span>Search accounts…</span>
                <span className="ml-auto font-mono text-[9px] text-white/30">⌘K</span>
              </div>
              <Bell className="w-4 h-4 text-white/50" />
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#4ECDC4] to-[#FFB020] flex items-center justify-center text-[10px] font-semibold text-[#050A1F]">
                AK
              </div>
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
            {[
              { l: 'In-market accounts', v: '281', d: '+42', c: '#4ECDC4' },
              { l: 'Buying committees', v: '1,247', d: '+18%', c: '#4ECDC4' },
              { l: 'Pipeline value', v: '$18.4M', d: '+2.1M', c: '#FFB020' },
              { l: 'AI confidence', v: '94.2%', d: '±0.4', c: '#FFB020' },
            ].map((k) => (
              <div key={k.l} className="bg-[#0A1024] p-4">
                <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-1.5">{k.l}</div>
                <div className="flex items-baseline gap-2">
                  <div className="text-2xl font-serif italic tabular text-[#F1EDE4]">{k.v}</div>
                  <div className="text-[10px] font-mono" style={{ color: k.c }}>
                    ▲ {k.d}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Main grid — chart + accounts table */}
          <div className="grid lg:grid-cols-3 gap-px bg-white/10">
            {/* Chart panel */}
            <div className="lg:col-span-2 bg-[#0A1024] p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Pipeline Velocity</div>
                  <div className="text-base font-medium text-[#F1EDE4] mt-0.5">
                    Multi-signal Accounts <span className="text-white/40 text-xs">· last 12 weeks</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {['1W', '4W', '12W', 'YTD'].map((t, i) => (
                    <span
                      key={t}
                      className={`text-[10px] px-2 py-1 rounded font-mono transition-colors ${
                        i === 2 ? 'bg-[#4ECDC4] text-[#050A1F]' : 'text-white/40 hover:text-white/70'
                      }`}
                    >
                      {t}
                    </span>
                  ))}
                  <Filter className="w-3.5 h-3.5 text-white/40 ml-2" />
                </div>
              </div>

              <svg viewBox="0 0 600 200" className="w-full h-40">
                <defs>
                  <linearGradient id="pgline" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#4ECDC4" stopOpacity="0.45" />
                    <stop offset="100%" stopColor="#4ECDC4" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="pgline2" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#FFB020" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#FFB020" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[0, 1, 2, 3].map((i) => (
                  <line
                    key={i}
                    x1="0"
                    x2="600"
                    y1={30 + i * 45}
                    y2={30 + i * 45}
                    stroke="rgba(255,255,255,0.06)"
                    strokeDasharray="2 4"
                  />
                ))}
                {/* baseline area */}
                <path
                  d="M0,170 L50,160 L100,165 L150,150 L200,155 L250,140 L300,145 L350,130 L400,135 L450,120 L500,125 L550,110 L600,115 L600,200 L0,200 Z"
                  fill="url(#pgline2)"
                />
                <path
                  d="M0,170 L50,160 L100,165 L150,150 L200,155 L250,140 L300,145 L350,130 L400,135 L450,120 L500,125 L550,110 L600,115"
                  fill="none"
                  stroke="#FFB020"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                  opacity="0.8"
                />
                {/* main area */}
                <path
                  d="M0,175 L50,155 L100,160 L150,130 L200,120 L250,105 L300,110 L350,80 L400,65 L450,55 L500,40 L550,30 L600,20 L600,200 L0,200 Z"
                  fill="url(#pgline)"
                />
                <path
                  d="M0,175 L50,155 L100,160 L150,130 L200,120 L250,105 L300,110 L350,80 L400,65 L450,55 L500,40 L550,30 L600,20"
                  fill="none"
                  stroke="#4ECDC4"
                  strokeWidth="2"
                />
                {[
                  [100, 160],
                  [250, 105],
                  [400, 65],
                  [600, 20],
                ].map(([x, y], i) => (
                  <g key={i}>
                    <circle cx={x} cy={y} r="8" fill="#4ECDC4" fillOpacity="0.2" />
                    <circle cx={x} cy={y} r="4" fill="#F1EDE4" stroke="#4ECDC4" strokeWidth="1.5" />
                  </g>
                ))}
                {/* callout */}
                <g>
                  <rect x="410" y="30" width="72" height="24" rx="4" fill="#0F1C40" stroke="#4ECDC4" strokeOpacity="0.5" />
                  <text
                    x="446"
                    y="45"
                    fontFamily="JetBrains Mono, monospace"
                    fontSize="10"
                    fill="#4ECDC4"
                    textAnchor="middle"
                  >
                    +42.6%
                  </text>
                </g>
                {/* x-axis */}
                <g fontFamily="JetBrains Mono, monospace" fontSize="8" fill="rgba(255,255,255,0.35)">
                  {['W1', 'W3', 'W5', 'W7', 'W9', 'W11'].map((w, i) => (
                    <text key={w} x={i * 110 + 30} y={195} textAnchor="middle">
                      {w}
                    </text>
                  ))}
                </g>
              </svg>

              <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/10">
                {[
                  { l: 'MQL → SQL', v: '3.1×', c: '#4ECDC4' },
                  { l: 'Avg. cycle', v: '17d', c: '#F1EDE4' },
                  { l: 'Signals /wk', v: '2,847', c: '#FFB020' },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="text-[9px] font-mono uppercase text-white/40">{s.l}</div>
                    <div className="text-xl font-serif italic tabular" style={{ color: s.c }}>
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right column: Accounts + Buying committee */}
            <div className="lg:col-span-1 bg-[#0A1024] p-5 space-y-5">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4]">Hot Accounts</div>
                  <MoreHorizontal className="w-3.5 h-3.5 text-white/40" />
                </div>
                <div className="space-y-2">
                  {[
                    { n: 'Boeing', s: 93, i: 'Aerospace' },
                    { n: 'AbbVie', s: 79, i: 'Pharma' },
                    { n: 'Cognizant', s: 67, i: 'IT Services' },
                    { n: 'Lockheed Martin', s: 65, i: 'Defense' },
                  ].map((a) => (
                    <div key={a.n} className="flex items-center justify-between text-xs bg-white/5 rounded-lg px-3 py-2">
                      <div>
                        <div className="text-[#F1EDE4] font-medium">{a.n}</div>
                        <div className="text-[9px] text-white/40 font-mono uppercase tracking-wider">{a.i}</div>
                      </div>
                      <div className="text-right">
                        <div
                          className="font-mono text-[13px]"
                          style={{ color: a.s > 80 ? '#FFB020' : '#4ECDC4' }}
                        >
                          {a.s}
                        </div>
                        <div className="text-[9px] text-white/40 font-mono">SCORE</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFB020]">Buying Committee</div>
                  <ArrowUpRight className="w-3.5 h-3.5 text-white/40" />
                </div>
                <div className="space-y-2">
                  {[
                    ['CIO', 92],
                    ['VP Engineering', 84],
                    ['Director IT', 78],
                    ['Head of Security', 65],
                  ].map(([role, score]) => (
                    <div key={role}>
                      <div className="flex justify-between text-[11px] mb-1">
                        <span className="text-white/80">{role}</span>
                        <span className="font-mono" style={{ color: score > 80 ? '#FFB020' : '#4ECDC4' }}>
                          {score}%
                        </span>
                      </div>
                      <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${score}%`,
                            background:
                              score > 80
                                ? 'linear-gradient(90deg, #4ECDC4, #FFB020)'
                                : '#4ECDC4',
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom AI insight strip */}
          <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-[#4ECDC4]/10 via-transparent to-[#FFB020]/10 border-t border-white/10">
            <div className="w-8 h-8 rounded-lg bg-[#FFB020]/20 border border-[#FFB020]/40 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-[#FFB020]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFB020] mb-0.5">
                AI Insight · updated 2m ago
              </div>
              <div className="text-[12px] text-[#F1EDE4]/90 leading-snug truncate">
                Boeing shows 3× funding + hiring signals this week — recommend accelerated outreach to CIO tier.
              </div>
            </div>
            <button className="hidden md:inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4] border border-[#4ECDC4]/40 px-3 py-1.5 rounded-full">
              View · <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
